import { useState } from "react"
import { toast } from "sonner"
import { useIsMutating, useQueryClient } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { useMessage, useThread, useThreadRuntime } from "@assistant-ui/react"
import { HugeiconsIcon } from "@hugeicons/react"
import { CheckmarkCircle02Icon, Edit01Icon } from "@hugeicons/core-free-icons"
import { submitDraft } from "../../api/assistant-api"
import { useChatPanel } from "../../hooks/use-chat-panel"
import { UPLOAD_ATTACHMENT_MUTATION_KEY } from "../../queries/assistant-queries"
import type { UIComponentConfirm } from "../../types/assistant.types"

interface ConfirmUIProps {
  data: UIComponentConfirm
}

export function ConfirmUI({ data }: ConfirmUIProps) {
  const threadRuntime = useThreadRuntime()
  const issueId = useChatPanel((s) => s.threadId) // holds DRAFT Issue id now
  const threadStatus = useChatPanel((s) => s.threadStatus)
  const setThreadId = useChatPanel((s) => s.setThreadId)
  const setThreadStatus = useChatPanel((s) => s.setThreadStatus)
  const clearAttachments = useChatPanel((s) => s.clearAttachments)
  const pendingCount = useChatPanel((s) => s.pendingAttachments.length)
  const pendingRefCount = useChatPanel((s) => s.pendingReferences.length)
  const uploadingCount = useIsMutating({ mutationKey: [...UPLOAD_ATTACHMENT_MUTATION_KEY] })
  const messageIndex = useMessage((s) => s.index)
  const messageCount = useThread((s) => s.messages.length)
  const qc = useQueryClient()
  const [answered, setAnswered] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Two non-interactive cases (mirrors choice-ui):
  //   1. Thread is closed (submitted / abandoned).
  //   2. The conversation has moved past this confirm card — i.e. there
  //      is at least one message after it. Either the user clicked
  //      "Edit details" and the AI responded, or they typed something
  //      else; either way the original "Ready to file" prompt is stale.
  //      Without this guard, reloading an active draft would let users
  //      file via an old confirm card.
  const threadClosed = threadStatus != null && threadStatus !== "DRAFT"
  const hasLaterMessage = messageIndex < messageCount - 1
  const isHistorical = threadClosed || hasLaterMessage
  // Confirm/file goes straight to ``submitDraft`` and skips the chat
  // turn that would persist any pending uploads or references. Same
  // guard as the header SubmitIssueButton: refuse while either kind
  // of staged context is unsent.
  const blockedByUploads = pendingCount > 0 || uploadingCount > 0
  const blockedByReferences = pendingRefCount > 0
  const blocked = blockedByUploads || blockedByReferences
  const blockedReason = blockedByUploads
    ? "Wait for uploads to finish, then send your message."
    : "Send your message first to attach those references."

  const handleSubmit = async () => {
    if (issueId == null) {
      toast.error("No draft to submit.")
      return
    }
    if (blocked) {
      toast.info(blockedReason)
      return
    }
    setAnswered(true)
    setSubmitting(true)

    try {
      const issue = await submitDraft(issueId)

      // Refresh history queries so the just-submitted issue surfaces
      // when the user opens the panel later.
      qc.invalidateQueries({ queryKey: ["chat-threads"] })
      qc.invalidateQueries({ queryKey: ["issues"] })

      // Pin status to OPEN *before* mutating the message tree — the
      // composer's read-only check keys off threadStatus, and a click
      // arriving between append and the status flip would otherwise
      // be allowed through.
      setThreadId(issue.id)
      setThreadStatus("OPEN")
      clearAttachments()

      // Append the success card rather than ``reset()``-ing the
      // runtime. ``reset`` was originally chosen for a clean
      // "you just submitted" view, but it tears down the messages
      // array out from under this very component — the
      // ``useMessage((s) => s.index)`` / ``useThread`` subscriptions
      // ConfirmUI uses to detect "this card is now historical" then
      // crash with ``tapClientLookup: Index N out of bounds``.
      // Appending leaves the conversation tree intact: the original
      // confirm card sees a later message and renders disabled
      // (its ``isHistorical`` flips), the composer is read-only via
      // threadStatus=OPEN, and the user can scroll back to review
      // what they filed.
      threadRuntime.append({
        role: "assistant",
        content: [
          { type: "text", text: "Your issue has been submitted." },
          {
            type: "data" as const,
            name: "reference",
            data: { component: "reference", reference: issue.reference },
          },
        ],
        startRun: false,
      })
      // The historical card stays mounted (we appended rather than
      // reset) — without clearing submitting it would render
      // "Submitting…" forever on a now-disabled card.
      setSubmitting(false)
    } catch {
      setAnswered(false)
      setSubmitting(false)
      toast.error("Failed to submit issue. Please try again.")
    }
  }

  const handleEdit = () => {
    setAnswered(true)
    threadRuntime.append("No, let me make changes")
  }

  return (
    <div className="mt-3 space-y-2.5">
      <div className="overflow-hidden rounded-xl border border-l-4 border-l-primary bg-background shadow-sm">
        <div className="flex items-center gap-2 border-b bg-primary/5 px-3.5 py-2">
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={14}
            className="text-primary"
          />
          <p className="text-xs font-semibold text-foreground">Ready to file</p>
        </div>
        {data.summary && (
          <p className="px-3.5 py-3 text-xs leading-relaxed whitespace-pre-wrap">
            {data.summary}
          </p>
        )}
        <p className="border-t bg-muted/30 px-3.5 py-2 text-[11px] text-muted-foreground">
          {data.label}
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="default"
          size="sm"
          className="h-8 cursor-pointer gap-1.5 text-xs"
          disabled={answered || isHistorical || blocked}
          onClick={handleSubmit}
          title={blocked ? blockedReason : undefined}
        >
          <HugeiconsIcon icon={CheckmarkCircle02Icon} size={13} />
          {submitting && !isHistorical ? "Submitting…" : "Confirm and file"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 cursor-pointer gap-1.5 text-xs"
          disabled={answered || isHistorical}
          onClick={handleEdit}
        >
          <HugeiconsIcon icon={Edit01Icon} size={13} />
          Edit details
        </Button>
      </div>
    </div>
  )
}
